import React, { useState, useCallback, useMemo } from 'react';
import { addDays, isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';
import PropTypes from 'prop-types';
import DayPicker from 'react-day-picker';
import NavbarElement from './NavbarElement';
import * as S from './styles';
import LoadInfo from './LoadInfo';

function ControlledDayPicker({
    numberOfMonths,
    startDate,
    endDate,
    onDatesChange,
    onMonthChange,
    initialMonth,
    offsetDays = 6,
    disabledDay,
    blockedDay,
    isDaysLoading = false,
}) {
    const [enterDate, changeEnterDate] = useState(null);
    const onDayMouseEnter = useCallback((date) => changeEnterDate(date), [changeEnterDate]);
    const onDayMouseLeave = useCallback(() => changeEnterDate(null), [changeEnterDate]);

    const onDayClick = useCallback(
        (date, mods) => {
            if (mods && (mods.disabled || mods.blocked)) {
                return;
            }
            onDatesChange({
                startDate: date,
                endDate: addDays(date, offsetDays),
            });
        },
        [onDatesChange, offsetDays]
    );

    const selectedDays = useCallback(
        (date) => {
            if (!startDate) {
                return false;
            }
            return (
                isSameDay(startDate, date) ||
                isSameDay(endDate || startDate, date) ||
                (isBefore(startDate, date) && isAfter(endDate || startDate, date))
            );
        },
        [startDate, endDate]
    );

    const disabledDays = useCallback(
        (date) => {
            if (isAfter(startOfDay(new Date()), date)) {
                return true;
            }
            if (typeof disabledDay === 'function') {
                return disabledDay(date);
            }
            return false;
        },
        [disabledDay]
    );

    const firstInRange = useCallback((date) => isSameDay(startDate, date), [startDate]);
    const lastInRange = useCallback(
        (date) => (startDate || endDate ? isSameDay(endDate || startDate, date) : false),
        [startDate, endDate]
    );

    const endOffset = useCallback(
        (date) => {
            if (!enterDate) {
                return false;
            }
            const offset = addDays(enterDate, offsetDays);
            return (
                (isSameDay(enterDate, date) || isBefore(enterDate, date)) &&
                (isSameDay(offset, date) || isAfter(offset, date))
            );
        },
        [enterDate, offsetDays]
    );

    const modifiers = useMemo(
        () => ({
            firstInRange,
            lastInRange,
            endOffset,
            ...(blockedDay ? { blocked: blockedDay } : {}),
        }),
        [firstInRange, lastInRange, endOffset, blockedDay]
    );

    return (
        <S.ControlledDayPicker>
            <React.Fragment>{isDaysLoading && <LoadInfo />}</React.Fragment>
            <DayPicker
                numberOfMonths={numberOfMonths}
                selectedDays={selectedDays}
                disabledDays={disabledDays}
                modifiers={modifiers}
                enableOutsideDaysClick={false}
                onDayMouseEnter={onDayMouseEnter}
                onDayMouseLeave={onDayMouseLeave}
                onDayClick={onDayClick}
                onMonthChange={onMonthChange}
                initialMonth={initialMonth}
                navbarElement={NavbarElement}
            />
        </S.ControlledDayPicker>
    );
}

export default React.memo(ControlledDayPicker);

ControlledDayPicker.propTypes = {
    startDate: PropTypes.instanceOf(Date).isRequired,
    endDate: PropTypes.instanceOf(Date).isRequired,
    numberOfMonths: PropTypes.number.isRequired,
    onDatesChange: PropTypes.func.isRequired,
    onMonthChange: PropTypes.func.isRequired,
    initialMonth: PropTypes.instanceOf(Date),
    offsetDays: PropTypes.number,
    disabledDay: PropTypes.func,
    blockedDay: PropTypes.func,
    isDaysLoading: PropTypes.bool,
};
